import { getCachedPayInputConfig } from "pages/PayRoll/Configuration/PayInputConfiguration/payInputConfigApi";
import { subModuleNameMatches } from "services/utils/HelperFunction";
import { nodeHasAccess, routeModuleName, screenHasAccess } from "services/utils/companyAccess";
import { pathAndProducts } from "services/constants";
import {
  CONFIG_GATED_FLYOUT_ITEMS,
  FLYOUT_CATEGORY_SUBMODULE_BY_MODULE,
  isSubmenuAccessible,
} from "components/Sidebar/sidebarAccess";
import { QUICK_ACTION_PARAM } from "./searchRegistry";

// Second check on every search result (pages, settings, quick actions,
// Recent, employees), on top of the server's own filtering: a result is
// shown only when the user's provisioning grants its screen. (The server
// returned My Workspace > Permission to a user without that access.)
//
// 1. The screen gate's own rule (ProductAccessGate), for every route of a
//    product: the product must be one of the user's products, and the
//    screen's module (routeModuleName) must be in the payload with access.
//    Pages without a sidebar row follow the product's access, as the gate.
// 2. When the route is a row inside a section (Approvals > Leave, My
//    Requests > Permission, Pay Inputs > Overtime, ...), that row's
//    submodule must be granted too, when the payload has one for it.
// 3. A Pay Input switched off in Pay Input Configuration is hidden.
// Only a route outside every product (none today) is left to the server.

const parseRoute = (route) => {
  const [path, query = ""] = String(route || "").split("?");
  const params = new URLSearchParams(query);
  params.delete(QUICK_ACTION_PARAM);
  return { path: path.replace(/\/+$/, "") || "/", params };
};

// Every param of `subset` has the same value in `params`.
const containsAll = (params, subset) => [...subset.entries()].every(([key, value]) => params.get(key) === value);

// The sidebar row for a route: { product, section, row } or null.
// Exact path + query wins; then a row whose query the route includes; then
// the row with the plain path; then the longest row path the route sits under.
export const findSidebarRow = (routes, route) => {
  const target = parseRoute(route);
  let best = null;
  const consider = (product, section, row) => {
    if (!row?.path || row.hideInSidebar) return;
    const candidate = parseRoute(row.path);
    let score = 0;
    if (candidate.path === target.path) {
      if (!containsAll(target.params, candidate.params)) return;
      score = candidate.params.toString() && containsAll(candidate.params, target.params)
        ? 3000
        : candidate.params.toString()
          ? 2000 + [...candidate.params.keys()].length
          : 1000;
    } else if (target.path.startsWith(`${candidate.path}/`) && !candidate.params.toString()) {
      score = candidate.path.length;
    } else {
      return;
    }
    if (!best || score > best.score) best = { product, section, row, score };
  };
  (routes || [])
    .filter((product) => !product?.sectionLabel && Array.isArray(product?.collapse))
    .forEach((product) =>
      product.collapse.forEach((section) => {
        const children = (section?.collapse || []).filter((row) => !row?.hideInSidebar);
        if (children.length) children.forEach((row) => consider(product, section, row));
        else consider(product, section, section);
      }),
    );
  return best;
};

// A row's submodule. Sections whose row names differ from the backend's
// (Approvals, My Requests, ...) use the sidebar's name maps, and a missing
// submodule means no access, as in the sidebar. Other sections match the
// row's own name; a row with no submodule of that name follows its section.
const subModuleGranted = (section, row, access) => {
  const moduleName = section?.permissionName || section?.name;
  const submodules = access?.module_details?.find((module) => module?.module_name === moduleName)?.submodules;
  const mapped = (FLYOUT_CATEGORY_SUBMODULE_BY_MODULE[section?.name] || {})[row?.name];
  if (mapped) {
    const submodule = submodules?.find((sub) => subModuleNameMatches(sub?.sub_module_name, mapped));
    return Boolean(submodule) && nodeHasAccess(submodule);
  }
  const submodule = submodules?.find(
    (sub) =>
      subModuleNameMatches(sub?.sub_module_name, row?.name) ||
      (row?.displayName && subModuleNameMatches(sub?.sub_module_name, row.displayName)),
  );
  return submodule ? nodeHasAccess(submodule) : true;
};

const configDisabled = (path, { domain, companyId }) => {
  const gated = CONFIG_GATED_FLYOUT_ITEMS.find((item) => item.path === path);
  return Boolean(gated && domain && companyId &&
    getCachedPayInputConfig(domain, companyId, gated.payInputType)?.enabled === false);
};

// true = may open, false = may not, null = that product's access isn't
// loaded yet (keep it hidden until it is).
export const canOpenRoute = (routes, accessByProduct, context, route) => {
  const { path } = parseRoute(route);
  const productName = pathAndProducts.find((item) => item.pathname === path.split("/")[2])?.productname;
  if (!productName) return true;
  // The user's products are the ones routes.js doesn't hide.
  const product = (routes || []).find(
    (entry) => entry?.name === productName && !entry?.sectionLabel && Array.isArray(entry?.collapse),
  );
  if (!product || product.hideInSidebar) return false;
  const access = accessByProduct[productName];
  if (!access) return null;
  if (!screenHasAccess(access, routeModuleName(routes, path, productName))) return false;

  const match = findSidebarRow(routes, route);
  if (!match || match.product.name !== productName) return true;
  const { section, row } = match;
  if (!isSubmenuAccessible(productName, section, access)) return false;
  if (row !== section) {
    if (!subModuleGranted(section, row, access)) return false;
    if (configDisabled(row.path, context)) return false;
  }
  return true;
};

// The products whose access the search needs (the user's own products).
export const searchProducts = (routes) =>
  (routes || []).filter((product) => !product?.sectionLabel && !product?.hideInSidebar && Array.isArray(product?.collapse));
